import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { BodyTypeService } from 'src/app/services/body-type/body-type.service';
import { BodyTypeModalService } from 'src/app/services/body-type-modal/body-type-modal.service';
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';

declare var bootstrap: any;

@Component({
  selector: 'app-body-types',
  templateUrl: './body-types.component.html',
  styleUrls: ['./body-types.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0%)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class BodyTypesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  bodyTypes: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedBodyTypes: string[] = [];

  constructor(
    private _bodyTypeService: BodyTypeService,
    private _bodyTypeModalService: BodyTypeModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadBodyTypes();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadBodyTypes(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._bodyTypeService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.bodyTypes = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.bodyTypes = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading body types:', error);
          this.bodyTypes = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadBodyTypes(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadBodyTypes(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadBodyTypes();
  }

  openAddBodyTypeModal(): void {
    this._bodyTypeModalService.clearSelectedBodyType();
    const modalElement = document.getElementById('kt_modal_body_type_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(bodyType: any): void {
    this._bodyTypeModalService.setSelectedBodyType(bodyType);
    const modalElement = document.getElementById('kt_modal_body_type_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteBodyType(bodyType: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${bodyType.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(bodyType);
      }
    });
  }

  private performDelete(bodyType: any): void {
    const bodyTypeId = bodyType._id || bodyType.id;
    this.subscriptions.push(
      this._bodyTypeService.DeleteDataById(bodyTypeId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: `"${bodyType.name}" has been deleted successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadBodyTypes(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete body type.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting body type.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onBodyTypeAdded(): void {
    console.log('Body type added, refreshing list...');
    this.loadBodyTypes(this.currentPage, this.searchTerm, this.filters);
  }

  onBodyTypeUpdated(): void {
    console.log('Body type updated, refreshing list...');
    this.loadBodyTypes(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Bulk selection methods
  toggleAllSelection(): void {
    if (this.selectedBodyTypes.length === this.bodyTypes.length) {
      this.selectedBodyTypes = [];
    } else {
      this.selectedBodyTypes = this.bodyTypes.map(bodyType => bodyType._id || bodyType.id);
    }
    this.toggleToolbars();
  }

  toggleSelection(bodyTypeId: string): void {
    const index = this.selectedBodyTypes.indexOf(bodyTypeId);
    if (index > -1) {
      this.selectedBodyTypes.splice(index, 1);
    } else {
      this.selectedBodyTypes.push(bodyTypeId);
    }
    this.toggleToolbars();
  }

  isSelected(bodyTypeId: string): boolean {
    return this.selectedBodyTypes.includes(bodyTypeId);
  }

  private toggleToolbars(): void {
    const baseToolbar = document.querySelector('[data-kt-body-type-table-toolbar="base"]');
    const selectedToolbar = document.querySelector('[data-kt-body-type-table-toolbar="selected"]');
    const selectedCount = document.querySelector('[data-kt-body-type-table-select="selected_count"]');

    if (this.selectedBodyTypes.length > 0) {
      baseToolbar?.classList.add('d-none');
      selectedToolbar?.classList.remove('d-none');
      if (selectedCount) {
        selectedCount.textContent = this.selectedBodyTypes.length.toString();
      }
    } else {
      baseToolbar?.classList.remove('d-none');
      selectedToolbar?.classList.add('d-none');
    }
  }

  bulkDelete(): void {
    if (this.selectedBodyTypes.length === 0) return;

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedBodyTypes.length} body type(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    this.subscriptions.push(
      this._bodyTypeService.BulkDelete(this.selectedBodyTypes).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: `${this.selectedBodyTypes.length} body type(s) have been deleted successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.selectedBodyTypes = [];
              this.loadBodyTypes(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete body types.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting body types.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }
}
